import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const GallerySection = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages = [
    {
      url: "https://images.unsplash.com/photo-1751651054934-3fbdf1d54d8b?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Catering spread setup",
      size: "large"
    },
    {
      url: "https://images.unsplash.com/photo-1603298573248-3e98441a0d6d?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Elegant appetizer platter",
      size: "small"
    },
    {
      url: "https://images.unsplash.com/photo-1576398607070-eb6395c862d9?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Beautifully plated desserts",
      size: "small"
    },
    {
      url: "https://images.unsplash.com/photo-1677129661598-98356ff05627?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Corporate event catering",
      size: "medium"
    },
    {
      url: "https://images.unsplash.com/photo-1555244162-803834f70033?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Fresh ingredients preparation",
      size: "medium"
    },
    {
      url: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Fine dining presentation",
      size: "large"
    }
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <section
      id="gallery"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFBF7] grain-overlay"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <p className="text-sm tracking-widest uppercase font-semibold text-orange-700">
            A glimpse into our approach
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-gray-800">
            Our Gallery
          </h2>
          <p className="text-lg text-gray-600 pt-4">
            Clean presentation, generous portions, and food made to be shared.
          </p>
        </div>

        {/* Bento Grid Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {/* Large item */}
          <div
            className="col-span-2 row-span-2 gallery-item rounded-2xl overflow-hidden cursor-pointer aspect-square"
            onClick={() => openLightbox(0)}
            data-testid="gallery-item-0"
          >
            <img
              src={galleryImages[0].url}
              alt={galleryImages[0].alt}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          {/* Small items */}
          <div
            className="gallery-item rounded-2xl overflow-hidden cursor-pointer aspect-square"
            onClick={() => openLightbox(1)}
            data-testid="gallery-item-1"
          >
            <img
              src={galleryImages[1].url}
              alt={galleryImages[1].alt}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          <div
            className="gallery-item rounded-2xl overflow-hidden cursor-pointer aspect-square"
            onClick={() => openLightbox(2)}
            data-testid="gallery-item-2"
          >
            <img
              src={galleryImages[2].url}
              alt={galleryImages[2].alt}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          {/* Medium items */}
          <div
            className="col-span-2 gallery-item rounded-2xl overflow-hidden cursor-pointer aspect-video"
            onClick={() => openLightbox(3)}
            data-testid="gallery-item-3"
          >
            <img
              src={galleryImages[3].url}
              alt={galleryImages[3].alt}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          <div
            className="col-span-2 gallery-item rounded-2xl overflow-hidden cursor-pointer aspect-video"
            onClick={() => openLightbox(4)}
            data-testid="gallery-item-4"
          >
            <img
              src={galleryImages[4].url}
              alt={galleryImages[4].alt}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          {/* Another large item */}
          <div
            className="col-span-2 gallery-item rounded-2xl overflow-hidden cursor-pointer aspect-video"
            onClick={() => openLightbox(5)}
            data-testid="gallery-item-5"
          >
            <img
              src={galleryImages[5].url}
              alt={galleryImages[5].alt}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          {/* Close button */}
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            onClick={closeLightbox}
            data-testid="lightbox-close"
          >
            <X size={24} />
          </button>

          {/* Navigation buttons */}
          <button
            className="absolute left-6 text-white/80 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>

          <button
            className="absolute right-6 text-white/80 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} />
          </button>

          {/* Image */}
          <div
            className="max-w-5xl max-h-[80vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[currentImageIndex].url}
              alt={galleryImages[currentImageIndex].alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-white/80 text-center mt-4">
              {galleryImages[currentImageIndex].alt}
            </p>
          </div>

          {/* Image counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
